"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface BlackFridayGeneratorProps {
  className?: string;
}

export default function BlackFridayGenerator({ className = "" }: BlackFridayGeneratorProps) {
  const [product, setProduct] = useState("");
  const [category, setCategory] = useState("");
  const [discount, setDiscount] = useState("70");
  const [targetAudience, setTargetAudience] = useState("");
  const [urgencyLevel, setUrgencyLevel] = useState("ALTO");
  const [platform, setPlatform] = useState("MÚLTIPLAS");
  const [offer, setOffer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const generateOffer = async () => {
    if (!product || !category || !targetAudience) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/black-friday-offer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          product, 
          category, 
          discount, 
          targetAudience, 
          urgencyLevel, 
          platform 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setOffer(data);
        addToast("🔥 Oferta Black Friday gerada com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao gerar oferta", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro ao gerar oferta", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast("📋 Copiado para área de transferência!", "success");
  };

  return (
    <div className={`bg-black text-green-400 rounded-lg p-6 border border-green-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-green-400">
          🔥 BLACK FRIDAY OFFER GENERATOR 🔥
        </h3>
        <div className="text-xs text-green-500 mb-4">
          [SYSTEM] v2.0.1 | STATUS: ONLINE | AUTH: ADMIN
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-green-500 mb-2">PRODUCT_NAME:</label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="w-full bg-black/50 border border-green-500/50 text-green-400 px-3 py-2 rounded focus:outline-none focus:border-green-400 focus:shadow-[0_0_10px_rgba(34,197,94,0.3)]"
            placeholder="ex: Curso de Marketing Digital"
          />
        </div>

        <div>
          <label className="block text-xs text-green-500 mb-2">CATEGORY:</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full bg-black/50 border border-green-500/50 text-green-400 px-3 py-2 rounded focus:outline-none focus:border-green-400"
          >
            <option value="">SELECT_CATEGORY</option>
            <option value="cursos">Cursos Online</option>
            <option value="software">Software/SaaS</option>
            <option value="ecommerce">E-commerce</option>
            <option value="servicos">Serviços</option>
            <option value="infoprodutos">Info Produtos</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-green-500 mb-2">DISCOUNT_%:</label>
          <select
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            className="w-full bg-black/50 border border-green-500/50 text-green-400 px-3 py-2 rounded focus:outline-none focus:border-green-400"
          >
            <option value="50">50% OFF</option>
            <option value="60">60% OFF</option>
            <option value="70">70% OFF</option>
            <option value="80">80% OFF</option>
            <option value="90">90% OFF</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-green-500 mb-2">TARGET_AUDIENCE:</label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full bg-black/50 border border-green-500/50 text-green-400 px-3 py-2 rounded focus:outline-none focus:border-green-400"
            placeholder="ex: Empreendedores iniciantes"
          />
        </div>

        <div>
          <label className="block text-xs text-green-500 mb-2">URGENCY_LEVEL:</label>
          <select
            value={urgencyLevel}
            onChange={(e) => setUrgencyLevel(e.target.value)}
            className="w-full bg-black/50 border border-green-500/50 text-green-400 px-3 py-2 rounded focus:outline-none focus:border-green-400"
          >
            <option value="BAIXO">LOW_PRIORITY</option>
            <option value="MEDIO">MEDIUM_PRIORITY</option>
            <option value="ALTO">HIGH_PRIORITY</option>
            <option value="CRITICO">CRITICAL_URGENCY</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-green-500 mb-2">PLATFORM:</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full bg-black/50 border border-green-500/50 text-green-400 px-3 py-2 rounded focus:outline-none focus:border-green-400"
          >
            <option value="MÚLTIPLAS">MULTI_PLATFORM</option>
            <option value="instagram">INSTAGRAM_ONLY</option>
            <option value="facebook">FACEBOOK_ONLY</option>
            <option value="email">EMAIL_ONLY</option>
            <option value="whatsapp">WHATSAPP_ONLY</option>
          </select>
        </div>
      </div>

      <button
        onClick={generateOffer}
        disabled={isLoading || !product || !category || !targetAudience}
        className="w-full bg-green-500/20 border border-green-500 text-green-400 py-3 px-6 rounded font-mono text-sm hover:bg-green-500/30 disabled:bg-gray-900/50 disabled:border-gray-700 disabled:text-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> GENERATING_OFFER...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            🔥 [EXECUTE_GENERATOR] 🔥
          </span>
        )}
      </button>

      {offer && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-green-500 mb-4">
            [OFFER_GENERATED_SUCCESSFULLY] | CONVERSION_SCORE: {offer.metadata?.conversion_score}%
          </div>
          
          <div className="bg-black/50 border border-green-500/30 rounded p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-lg font-bold text-green-400">TITLE:</h4>
              <button
                onClick={() => copyToClipboard(offer.title)}
                className="text-xs bg-green-500/20 px-2 py-1 rounded hover:bg-green-500/30"
              >
                [COPY]
              </button>
            </div>
            <div className="text-green-300 mb-4">{offer.title}</div>
            
            <div className="mb-4">
              <h5 className="text-sm font-bold text-green-400 mb-2">DESCRIPTION:</h5>
              <div className="text-green-300 text-sm leading-relaxed">{offer.description}</div>
            </div>

            {offer.price && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-red-900/30 border border-red-500/50 rounded p-3">
                  <div className="text-xs text-red-400 mb-1">ORIGINAL_PRICE:</div>
                  <div className="text-lg font-bold text-red-400 line-through">
                    R$ {offer.price.original}
                  </div>
                </div>
                <div className="bg-green-900/30 border border-green-500/50 rounded p-3">
                  <div className="text-xs text-green-400 mb-1">DISCOUNTED_PRICE:</div>
                  <div className="text-2xl font-bold text-green-400">
                    R$ {offer.price.discounted}
                  </div>
                </div>
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded p-3">
                  <div className="text-xs text-yellow-400 mb-1">INSTALLMENTS:</div>
                  <div className="text-lg font-bold text-yellow-400">
                    {offer.price.installments}
                  </div>
                </div>
              </div>
            )}

            {offer.urgency && (
              <div className="bg-red-900/20 border border-red-500/30 rounded p-3 mb-4">
                <h5 className="text-sm font-bold text-red-400 mb-2">⚠️ URGENCY_TRIGGERS:</h5>
                <div className="space-y-1 text-sm">
                  <div className="text-red-300">⏰ {offer.urgency.message}</div>
                  <div className="text-red-300">📦 {offer.urgency.stock}</div>
                  <div className="text-red-300">⏳ TIMER: {offer.urgency.timer}</div>
                </div>
              </div>
            )}

            {offer.bonuses && (
              <div className="bg-purple-900/20 border border-purple-500/30 rounded p-3 mb-4">
                <h5 className="text-sm font-bold text-purple-400 mb-2">🎁 BONUS_STACK:</h5>
                <div className="space-y-1 text-sm">
                  {offer.bonuses.map((bonus: string, index: number) => (
                    <div key={index} className="text-purple-300">• {bonus}</div>
                  ))}
                </div>
              </div>
            )}

            {offer.cta && (
              <div className="bg-green-900/20 border border-green-500/30 rounded p-3 mb-4">
                <h5 className="text-sm font-bold text-green-400 mb-2">🚀 CALL_TO_ACTION:</h5>
                <div className="space-y-2">
                  <div className="text-green-300 font-bold">MAIN: {offer.cta.main}</div>
                  <div className="text-green-300">SECONDARY: {offer.cta.secondary}</div>
                  <div className="text-green-300">EMERGENCY: {offer.cta.emergency}</div>
                </div>
              </div>
            )}

            {offer.hashtags && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3">
                <h5 className="text-sm font-bold text-blue-400 mb-2"># HASHTAGS:</h5>
                <div className="flex flex-wrap gap-2">
                  {offer.hashtags.map((tag: string, index: number) => (
                    <span key={index} className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
